import { blogPosts, BlogPost } from "./blogPosts";
import { Capsula } from "./capsulas";
import { LucideIcon, Video, MessageSquare, Mail, Presentation, Phone, Brain, Sparkles, Users, Zap } from "lucide-react";

/**
 * Genera cápsulas dinámicas agrupando artículos del blog
 * por tema, audiencia o problema de negocio
 */
export function generateCapsulasFromBlog(): Capsula[] {
  const autoGeneratedCapsulas: Capsula[] = [];
  
  // Agrupar artículos por capsulaTheme
  const themeGroups = new Map<string, BlogPost[]>();
  
  blogPosts.forEach(post => {
    if (post.capsulaTheme) {
      const existing = themeGroups.get(post.capsulaTheme) || [];
      themeGroups.set(post.capsulaTheme, [...existing, post]);
    }
  });
  
  // Generar una cápsula por cada grupo
  themeGroups.forEach((posts, theme) => {
    if (posts.length >= 2) { // Mínimo 2 artículos para crear cápsula
      const firstPost = posts[0];
      
      // Detectar dificultad promedio
      const difficulties = posts
        .map(p => p.diyDifficulty)
        .filter(Boolean);
      const hasComplex = difficulties.includes("Requiere paciencia");
      
      // Detectar tiempo promedio
      const times = posts
        .map(p => p.avgImplementationTime)
        .filter(Boolean);
      const avgTime = times.length > 0 ? times[0] : "30 minutos";
      
      autoGeneratedCapsulas.push({
        id: `${theme}-auto`,
        icon: getIconForTheme(theme),
        title: generatePYMETitle(theme, firstPost),
        description: generatePYMEDescription(posts),
        difficulty: hasComplex ? "Media" : "Fácil",
        timeToImplement: avgTime,
        relatedPostIds: posts.map(p => p.id),
        category: firstPost.category || "Nuevas Herramientas",
        isAutoGenerated: true,
      } as Capsula & { isAutoGenerated: boolean });
    }
  });
  
  return autoGeneratedCapsulas;
}

/**
 * Genera título con tono PYME
 */
function generatePYMETitle(theme: string, post: BlogPost): string {
  if (post.businessSolution) {
    return post.businessSolution;
  }
  
  // Fallback: convertir theme a título legible
  const titles: Record<string, string> = {
    "video-creation": "Crea Videos sin Cámara ni Edición",
    "whatsapp-automation": "WhatsApp que Trabaja Solo",
    "email-automation": "Emails que se Responden Solos",
    "presentation-creation": "Presentaciones que se Crean Solas",
    "voice-agents": "Atención Telefónica Automática",
    "document-analysis": "Consulta Documentos al Instante",
  };
  
  return titles[theme] || post.title;
}

/**
 * Genera descripción con tono PYME
 */
function generatePYMEDescription(posts: BlogPost[]): string {
  const firstPost = posts[0];
  
  if (firstPost.businessProblem) {
    return `${firstPost.businessProblem} Descubre ${posts.length} herramientas que otros negocios ya están usando.`;
  }
  
  return `${posts.length} herramientas probadas por pequeñas empresas como la tuya. Sin complicaciones técnicas.`;
}

/**
 * Asigna icono basado en el tema
 */
function getIconForTheme(theme: string): LucideIcon {
  const icons: Record<string, LucideIcon> = {
    "video-creation": Video,
    "whatsapp-automation": MessageSquare,
    "email-automation": Mail,
    "presentation-creation": Presentation,
    "voice-agents": Phone,
    "document-analysis": Brain,
    "visual-design": Sparkles,
    "customer-management": Users,
  };
  
  return icons[theme] || Zap;
}

/**
 * Combina cápsulas manuales con auto-generadas
 */
export function getAllCapsulas(manualCapsulas: Capsula[]): Capsula[] {
  const autoGenerated = generateCapsulasFromBlog();
  
  // Marcar las manuales como tal
  const marked = manualCapsulas.map(c => ({ ...c, isManual: true }));
  
  return [...marked, ...autoGenerated];
}
